import { PeTypes } from '@platform/types';
import React, { useContext, useEffect, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import { Loader } from '../components/shared';
import handleJobType from '../helpers/jobTypeHandler';
import { useWorkspace } from '../hooks';
import useWorkspaceSubscription from '../hooks/useWorkspaceSubscription';
import NoWorkspacePage from '../pages/NoWorkspace.page';
import SubscriptionExpired from '../pages/SubscriptionExpired.page';
import { setWorkspace } from '../pe.api';
import { useSessionContext } from './SessionContext';

export const WORKSPACE_STORAGE_ITEM_ID = 'wid';

interface ContextState {
  activeWorkspace: PeTypes.Workspace;
}

const DEFAULT_VALUE: ContextState = {
  activeWorkspace: { id: -1, description: '', availableSurveys: [], name: 'dummy', imageURL: '', isAdmin: false },
};

const WorkspaceContext = React.createContext<ContextState>(DEFAULT_VALUE);

interface Props {
  workspaceId: number;
  children: React.ReactNode;
}

export const WorkspaceContextProvider: React.FC<Props> = ({ workspaceId, children }) => {
  const queryClient = useQueryClient();
  const workspaceSubscriptionQuery = useWorkspaceSubscription(workspaceId);
  const workspaceQuery = useWorkspace(workspaceId);
  const { notify, registerRealTimeListener, user } = useSessionContext();

  useEffect(() => {
    if (!user) return;
    const unregister = registerRealTimeListener('jobCompleted', (msg: PeTypes.GeoBufferJobNotification) =>
      handleJobType(msg, queryClient, notify, user.id, workspaceId)
    );

    return () => {
      unregister();
    };
  }, [workspaceId, user]);

  useEffect(() => {
    // store workspaceId into localStorage (used by peApi and full page reloads)
    localStorage.setItem(WORKSPACE_STORAGE_ITEM_ID, workspaceId.toString());
    setWorkspace(workspaceId);
  }, [workspaceId]);

  const memoizedState = useMemo(
    () => ({
      activeWorkspace: workspaceQuery.data ?? DEFAULT_VALUE.activeWorkspace,
    }),
    [workspaceQuery.data]
  );

  if (workspaceQuery.isError || workspaceSubscriptionQuery.error) return <NoWorkspacePage />;

  if (
    workspaceQuery.isLoading ||
    workspaceQuery.isIdle ||
    workspaceSubscriptionQuery.isLoading ||
    workspaceSubscriptionQuery.isIdle
  ) {
    return <Loader>Loading workspace details</Loader>;
  }

  if (!workspaceSubscriptionQuery.data?.hasActiveLicence) return <SubscriptionExpired id={workspaceId} />;

  return <WorkspaceContext.Provider value={memoizedState}>{children}</WorkspaceContext.Provider>;
};

export const useWorkspaceContext = (): ContextState => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error(`useWorkspaceContext must be used within a WorkspaceContextProvider`);
  }
  return context;
};
